import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useSampleMessageActions } from './useSampleMessageActions';
import {
  Button,
  ConversationFeed,
  EmptyState,
  LoadMore,
  MessageSkeleton,
  MessageThread,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import type { ConversationMessage } from '@/lib/conversation';
import { FILES, MARKUP_SAMPLES, THREADS } from './model';
import { MessageContent } from '@/components/ui/MessageContent';

export function ConversationDemo() {
  const actions = useSampleMessageActions();
  const [count, setCount] = useState(6);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(['discussion']));
  const [reactions, setReactions] = useState<Record<string, NonNullable<ConversationMessage['reactions']>>>(
    {},
  );
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [automatic, setAutomatic] = useState(false);
  const [empty, setEmpty] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const load = useCallback(() => {
    if (timer.current || count >= THREADS.length) return;
    setState('loading');
    timer.current = setTimeout(() => {
      setCount((value) => Math.min(value + 6, THREADS.length));
      setState('idle');
      timer.current = null;
    }, 1400);
  }, [count]);
  function simulateError() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setState('error');
  }
  function toggleReaction(id: string, reactionId: string) {
    const message = THREADS.flatMap((thread) => [thread.message, ...(thread.replies ?? [])]).find(
      (message) => message.id === id,
    );
    if (!message?.reactions) return;
    setReactions((current) => {
      const items = current[id] ?? message.reactions!;
      return {
        ...current,
        [id]: items.map((reaction) =>
          reaction.id !== reactionId
            ? reaction
            : {
                ...reaction,
                selected: !reaction.selected,
                count: reaction.count + (reaction.selected ? -1 : 1),
                people: reaction.selected
                  ? reaction.people?.filter((name) => name !== 'Демо Пользователь')
                  : [...(reaction.people ?? []), 'Демо Пользователь'],
              },
        ),
      };
    });
  }
  const patchMessage = (message: ConversationMessage): ConversationMessage =>
    reactions[message.id] ? { ...message, reactions: reactions[message.id] } : message;
  return (
    <section aria-label="Обсуждение макета" className="min-w-0">
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-line bg-muted p-3">
        <span className="type-overline text-fg-muted">Состояния образца</span>
        <Button variant="ghost" size="sm" onClick={() => setEmpty((value) => !value)}>
          {empty ? 'Вернуть переписку' : 'Пустая лента'}
        </Button>
        <Button variant="ghost" size="sm" onClick={simulateError} disabled={empty || count >= THREADS.length}>
          Ошибка подгрузки
        </Button>
        <label className="ml-auto flex items-center gap-2 text-xs text-fg-muted">
          <Switch checked={automatic} onCheckedChange={setAutomatic} aria-label="Автоматическая подгрузка" />
          Подгружать автоматически
        </label>
      </div>
      {empty ? (
        <EmptyState
          icon={<MessageSquare />}
          title="Сообщений пока нет"
          description="Здесь появится переписка, когда участники начнут обсуждение."
          action={{ label: 'Вернуть переписку', onClick: () => setEmpty(false) }}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-line bg-card">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div>
              <h3 className="type-subheading">Общий макет</h3>
              <p className="mt-1 text-xs text-fg-muted">23 октября 2026 · время МСК</p>
            </div>
            <span className="text-xs text-fg-muted">Чат · Почта · Обсуждение</span>
          </header>
          <ConversationFeed>
            {THREADS.slice(0, count).map((thread) => (
              <li key={thread.message.id}>
                <MessageThread
                  thread={{
                    message: patchMessage(thread.message),
                    replies: thread.replies?.map(patchMessage),
                  }}
                  expanded={expanded.has(thread.message.id)}
                  onExpandedChange={(open) =>
                    setExpanded((current) => {
                      const next = new Set(current);
                      if (open) next.add(thread.message.id);
                      else next.delete(thread.message.id);
                      return next;
                    })
                  }
                  {...actions}
                  onReaction={toggleReaction}
                />
              </li>
            ))}
            {state === 'loading' &&
              Array.from({ length: Math.min(6, THREADS.length - count) }, (_, i) => (
                <li key={'loading-' + i}>
                  <MessageSkeleton />
                </li>
              ))}
          </ConversationFeed>
          <div className="border-t border-line">
            <LoadMore
              state={count === THREADS.length ? 'complete' : state}
              onLoad={load}
              loaded={count}
              total={THREADS.length}
              automatic={automatic}
              itemLabel="сообщения"
            />
          </div>
        </div>
      )}
    </section>
  );
}

export function MarkupDemo() {
  const actions = useSampleMessageActions();
  return (
    <section className="grid min-w-0 grid-cols-1 gap-4" aria-label="Разметка сообщений">
      {MARKUP_SAMPLES.map((sample) => (
        <article
          key={sample.title}
          className="min-w-0 overflow-hidden rounded-lg border border-line bg-card p-5"
        >
          <header className="mb-4">
            <h3 className="type-subheading">{sample.title}</h3>
            <p className="mt-1 text-xs text-fg-muted">{sample.description}</p>
          </header>
          <Tabs defaultValue="rendered" variant="pill">
            <TabsList aria-label={sample.title}>
              <TabsTrigger value="rendered">Как выглядит</TabsTrigger>
              <TabsTrigger value="source">Исходный текст</TabsTrigger>
            </TabsList>
            <TabsContent value="rendered" className="mt-4">
              <MessageContent content={sample.content} {...actions} />
            </TabsContent>
            <TabsContent value="source" className="mt-4">
              <pre
                tabIndex={0}
                className="max-h-80 overflow-auto rounded-sm bg-muted p-4 font-mono text-xs leading-5 break-all whitespace-pre-wrap focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                {sample.content.text}
              </pre>
            </TabsContent>
          </Tabs>
        </article>
      ))}
      <p className="text-xs text-fg-muted">
        Вложения во всех источниках открываются одинаково: {FILES.map((file) => file.name).join(' · ')}.
      </p>
    </section>
  );
}
